import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Product meta (Availability + SKU) that stays in sync with the selected variant.
 * Uses the same section-level variant:update + morph pattern as Horizon product-price / product-sku.
 */
class OborProductMeta extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section?.addEventListener('change', this.#onPickerChange);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section?.removeEventListener('change', this.#onPickerChange);
    this.#section = null;
  }

  /**
   * Immediate update from the clicked option (does not wait for section fetch).
   * @param {Event} event
   */
  #onPickerChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('variant-picker')) return;

    const option =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!(option instanceof HTMLElement)) return;

    const picker = target.closest('variant-picker');
    if (
      picker instanceof HTMLElement &&
      picker.dataset.productId &&
      this.dataset.productId &&
      picker.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const sku = option.dataset.variantSku;
    const availableAttr = option.dataset.variantAvailable;
    const variantId = option.dataset.variantId;

    if (sku != null || availableAttr != null) {
      this.#paint({
        sku: sku ?? this.#skuFromMap(variantId),
        available: availableAttr == null ? this.#availableFromMap(variantId) : availableAttr === 'true',
      });
      return;
    }

    if (variantId) {
      this.#paintFromMap(variantId);
    }
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const variant = event.detail.resource;
    if (variant) {
      this.#paint({
        sku: variant.sku || this.#skuFromMap(variant.id) || '',
        available: Boolean(variant.available),
      });
    }

    const html = event.detail.data.html;
    if (!html) return;

    const selector = this.dataset.blockId
      ? `obor-product-meta[data-block-id="${this.dataset.blockId}"]`
      : `obor-product-meta[data-product-id="${this.dataset.productId}"]`;
    const next = html.querySelector(selector);
    if (!next) return;

    morph(this, next, { childrenOnly: true });
  };

  /**
   * @param {{sku?: string, available?: boolean}} data
   */
  #paint({ sku, available }) {
    const skuEl = this.querySelector('[data-obor-meta-sku]');
    const skuLine = this.querySelector('[data-obor-meta-sku-line]');
    const availEl = this.querySelector('[data-obor-meta-availability]');

    if (skuEl && skuLine && sku != null) {
      skuEl.textContent = sku;
      skuLine.hidden = !sku;
    }

    if (availEl && typeof available === 'boolean') {
      const inStockText = this.dataset.inStockText || 'In stock';
      const outOfStockText = this.dataset.outOfStockText || 'Out of stock';
      availEl.textContent = available ? inStockText : outOfStockText;
      availEl.classList.toggle('obor-product-meta__value--in-stock', available);
      availEl.classList.toggle('obor-product-meta__value--out', !available);
    }
  }

  /** @param {string | undefined} variantId */
  #paintFromMap(variantId) {
    if (!variantId) return;
    this.#paint({
      sku: this.#skuFromMap(variantId),
      available: this.#availableFromMap(variantId),
    });
  }

  /** @param {string | number | undefined} variantId */
  #getMapEntry(variantId) {
    if (variantId == null) return null;
    const script = this.querySelector('[data-obor-meta-variants]');
    if (!script?.textContent) return null;
    try {
      const map = JSON.parse(script.textContent);
      return map[String(variantId)] || null;
    } catch {
      return null;
    }
  }

  /** @param {string | number | undefined} variantId */
  #skuFromMap(variantId) {
    return this.#getMapEntry(variantId)?.sku || '';
  }

  /** @param {string | number | undefined} variantId */
  #availableFromMap(variantId) {
    const entry = this.#getMapEntry(variantId);
    return typeof entry?.available === 'boolean' ? entry.available : true;
  }
}

if (!customElements.get('obor-product-meta')) {
  customElements.define('obor-product-meta', OborProductMeta);
}
